import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NetworkConnectionLost } from './Pages/components/NetworkConnectionLost';
import PracticeDesktop from './Pages/Practice/PracticeDesktop';
import PracticeMobile from './Pages/Practice/PracticeMobile';
import { isMobileOrTabletDevice } from '../utils/helper';
import { useConnectionState } from '@livekit/components-react';

const PracticeRoom = ({ disconnect, isConnected }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const roomState = useConnectionState();
    const hasCheckedStatus = useRef(false);
    const { roomId, role } = useParams();
    const [timer, setTimer] = useState(300);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [onlineStatus, setOnlineStatus] = useState(true);
    const [form] = useState(location?.state?.form);

    useEffect(() => {
        let countdown;
        if (isConnected) {
            // Start the countdown
            if (roomState === 'disconnected') {
                handleWaitEndCall();
            } else {
                countdown = setInterval(() => {
                    setTimer((prev) => {
                        if (prev <= 1) {
                            clearInterval(countdown);
                            handleWaitEndCall();
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            }
        } else {
            // Clear the timer when disconnected
            clearInterval(countdown);
            setTimer(300); // Reset to 5 minutes
        }

        return () => clearInterval(countdown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomState, isConnected]);

    const handleEndCall = async () => {
        if (roomState !== 'disconnected') await disconnect();
        navigate(`/room/preview/${role}/${roomId}`, { state: form });
    };

    const handleWaitEndCall = async () => {
        if (roomState !== 'disconnected') await disconnect();
        setTimeout(async () => {
            navigate(`/room/preview/${role}/${roomId}`, { state: form });
        }, 6000);
    };

    useEffect(() => {
        if (!hasCheckedStatus.current && !location?.state?.status) {
            navigate(`/room/preview/${role}/${roomId}`, { state: form });
        } else {
            hasCheckedStatus.current = true;
            navigate(location.pathname, {
                replace: true,
                state: { ...location.state, status: false },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.state?.status]);

    useEffect(() => {
        const handleTimeout = () => {
            if (!onlineStatus) setIsNetworkError(true);
        };
        const timeoutId = setTimeout(handleTimeout, 10000);
        return () => clearTimeout(timeoutId);
    }, [onlineStatus]);

    const offlineCallback = () => setOnlineStatus(false);
    const onlineCallback = () => setOnlineStatus(true);

    useEffect(() => {
        window.addEventListener('offline', offlineCallback);
        window.addEventListener('online', onlineCallback);
        return () => {
            window.removeEventListener('offline', offlineCallback);
            window.removeEventListener('online', onlineCallback);
        };
    }, []);

    useEffect(() => {
        if (onlineStatus && isNetworkError) handleEndCall(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineStatus, isNetworkError]);

    return isNetworkError ? (
        <NetworkConnectionLost inInterviewRoom />
    ) : isMobileOrTabletDevice() ? (
        <PracticeMobile form={form} handleEndCall={handleEndCall} timer={timer} />
    ) : (
        <PracticeDesktop form={form} handleEndCall={handleEndCall} timer={timer} />
    );
};

export default PracticeRoom;
